import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft, Calendar, Users, MapPin, FileText, Plus, Minus, ShoppingBag } from 'lucide-react';
interface QuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialItem?: string;
}
const eventTypes = ['Wedding', 'Corporate Event', 'Private Party', 'Community Event', 'Meal Prep'];
const cateringPackages = [{
  id: 'p1',
  name: 'Signature Jollof Fiesta',
  serves: 50,
  description: 'Premium Jollof rice, plantains, and choice of two proteins (Chicken/Beef).'
}, {
  id: 'p2',
  name: 'Corporate Suya Bundle',
  serves: 20,
  description: 'Assorted Suya skewers, gourmet meat pies, and fresh side salad.'
}, {
  id: 'p3',
  name: 'Vegan Heritage Package',
  serves: 20,
  description: 'Cassava leaf stew, white rice, fried plantains, and mixed vegetables.'
}, {
  id: 'p4',
  name: 'Intimate Gathering Box',
  serves: 10,
  description: 'Curated selection of appetizers, main course, and dessert bites.'
}];
const availableAddons = [{
  id: 'a1',
  name: 'Bottled Water (Case of 24)'
}, {
  id: 'a2',
  name: 'Assorted Sodas (Case of 24)'
}, {
  id: 'a3',
  name: 'Premium Table Linens (Per Table)'
}, {
  id: 'a4',
  name: 'Chafing Dish Rental (Includes Fuel)'
}, {
  id: 'a5',
  name: 'Event Decor & Balloon Garland'
}, {
  id: 'a6',
  name: 'Gourmet Cinnamon Rolls (Dozen)'
}];
export function QuoteModal({
  isOpen,
  onClose,
  initialItem
}: QuoteModalProps) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    eventType: '',
    attendees: '',
    date: '',
    location: '',
    name: '',
    email: '',
    notes: ''
  });
  const [packageQty, setPackageQty] = useState<Record<string, number>>({});
  const [addonQty, setAddonQty] = useState<Record<string, number>>({});
  useEffect(() => {
    if (isOpen) {
      let mappedType = '';
      if (initialItem) {
        const lowerItem = initialItem.toLowerCase();
        if (lowerItem.includes('wedding')) mappedType = 'Wedding';else if (lowerItem.includes('corporate')) mappedType = 'Corporate Event';else if (lowerItem.includes('meal prep')) mappedType = 'Meal Prep';else if (eventTypes.includes(initialItem)) mappedType = initialItem;
      }
      setFormData({
        eventType: mappedType,
        attendees: '',
        date: '',
        location: '',
        name: '',
        email: '',
        notes: ''
      });
      setStep(1);
      setPackageQty({});
      setAddonQty({});
    }
  }, [isOpen, initialItem]);
  const nextStep = () => setStep(s => s + 1);
  const prevStep = () => setStep(s => s - 1);
  const updateQty = (type: 'package' | 'addon', id: string, delta: number) => {
    const setter = type === 'package' ? setPackageQty : setAddonQty;
    setter(prev => {
      const current = prev[id] || 0;
      const next = Math.max(0, current + delta);
      return {
        ...prev,
        [id]: next
      };
    });
  };
  const totalServings = cateringPackages.reduce((total, pkg) => {
    return total + pkg.serves * (packageQty[pkg.id] || 0);
  }, 0);
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-185">
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6" data-id="element-186">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-brand-black/80 backdrop-blur-sm" data-id="element-187" />

        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative bg-white w-full max-w-3xl rounded-sm overflow-hidden shadow-2xl flex flex-col max-h-[90vh]" data-id="element-188">
          {/* Header */}
          <div className="bg-brand-black p-6 flex justify-between items-center shrink-0" data-id="element-189">
            <div data-id="element-190">
              <h2 className="text-white font-serif text-2xl" data-id="element-191">
                Catering Order Request
              </h2>
              <p className="text-brand-gold text-xs uppercase tracking-widest mt-1" data-id="element-192">
                Step {step} of 4
              </p>
            </div>
            <button onClick={onClose} className="text-white/50 hover:text-white transition-colors" data-id="element-193">
              <X size={24} data-id="element-194" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="h-1 bg-gray-100 w-full shrink-0" data-id="element-195">
            <motion.div className="h-full bg-brand-gold" initial={{
            width: '25%'
          }} animate={{
            width: `${step / 4 * 100}%`
          }} data-id="element-196" />
          </div>

          <div className="p-4 md:p-8 overflow-y-auto flex-grow" data-id="element-197">
            {step === 1 && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} data-id="element-198">
                <h3 className="text-xl md:text-2xl mb-6 font-serif" data-id="element-199">
                  Event Details
                </h3>
                <div className="space-y-6" data-id="element-200">
                  <div data-id="element-201">
                    <label className="block text-xs md:text-sm font-bold uppercase tracking-widest text-gray-500 mb-3" data-id="element-202">
                      Type of Event
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-203">
                      {eventTypes.map(type => <button key={type} onClick={() => setFormData({
                    ...formData,
                    eventType: type
                  })} className={`p-3 text-sm border rounded-sm transition-all ${formData.eventType === type ? 'border-brand-black bg-brand-black text-white' : 'border-gray-200 hover:border-brand-gold text-gray-700'}`} data-id="element-204">
                          {type}
                        </button>)}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-205">
                    <div className="relative" data-id="element-206">
                      <Users className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-207" />
                      <input type="number" placeholder="Number of Attendees" value={formData.attendees} className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-sm focus:border-brand-gold outline-none" onChange={e => setFormData({
                    ...formData,
                    attendees: e.target.value
                  })} data-id="element-208" />
                    </div>
                    <div className="relative" data-id="element-209">
                      <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-210" />
                      <input type="date" value={formData.date} className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-sm focus:border-brand-gold outline-none" onChange={e => setFormData({
                    ...formData,
                    date: e.target.value
                  })} data-id="element-211" />
                    </div>
                  </div>
                  <div className="relative" data-id="element-212">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-213" />
                    <input type="text" placeholder="Event Location (DMV Area)" value={formData.location} className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-sm focus:border-brand-gold outline-none" onChange={e => setFormData({
                  ...formData,
                  location: e.target.value
                })} data-id="element-214" />
                  </div>
                </div>
              </motion.div>}

            {step === 2 && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} data-id="element-215">
                <div className="flex flex-col sm:flex-row justify-between sm:items-end mb-6 gap-4" data-id="element-216">
                  <div data-id="element-217">
                    <h3 className="text-xl md:text-2xl font-serif" data-id="element-218">
                      Select Packages
                    </h3>
                    <p className="text-gray-500 text-sm mt-1" data-id="element-219">
                      Combine packages to meet your guest count.
                    </p>
                  </div>
                  <div className="text-left sm:text-right bg-gray-50 p-3 rounded-sm sm:bg-transparent sm:p-0" data-id="element-220">
                    <p className="text-xs uppercase tracking-widest text-gray-400" data-id="element-221">
                      Total Servings
                    </p>
                    <p className={`text-xl font-bold ${totalServings < parseInt(formData.attendees || '0') ? 'text-red-500' : 'text-green-600'}`} data-id="element-222">
                      {totalServings} / {formData.attendees || 0}
                    </p>
                  </div>
                </div>

                <div className="space-y-4" data-id="element-223">
                  {cateringPackages.map(pkg => <div key={pkg.id} className={`p-4 border rounded-sm transition-colors ${packageQty[pkg.id] ? 'border-brand-gold bg-brand-gold/5' : 'border-gray-200'}`} data-id="element-224">
                      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4" data-id="element-225">
                        <div className="flex-grow" data-id="element-226">
                          <h4 className="font-bold text-brand-black" data-id="element-227">
                            {pkg.name}
                          </h4>
                          <p className="text-sm text-gray-500 mt-1" data-id="element-228">
                            {pkg.description}
                          </p>
                          <span className="inline-block mt-2 text-xs font-bold uppercase tracking-widest bg-gray-100 px-2 py-1 rounded-sm text-gray-600" data-id="element-229">
                            Serves {pkg.serves}
                          </span>
                        </div>
                        <div className="flex items-center justify-between sm:justify-start gap-3 bg-white border border-gray-200 rounded-sm p-1 shrink-0 self-start sm:self-auto w-full sm:w-auto mt-2 sm:mt-0" data-id="element-230">
                          <button onClick={() => updateQty('package', pkg.id, -1)} className="p-2 hover:text-brand-gold transition-colors" data-id="element-231">
                            <Minus size={16} data-id="element-232" />
                          </button>
                          <span className="w-8 text-center font-bold" data-id="element-233">
                            {packageQty[pkg.id] || 0}
                          </span>
                          <button onClick={() => updateQty('package', pkg.id, 1)} className="p-2 hover:text-brand-gold transition-colors" data-id="element-234">
                            <Plus size={16} data-id="element-235" />
                          </button>
                        </div>
                      </div>
                    </div>)}
                </div>
              </motion.div>}

            {step === 3 && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} data-id="element-236">
                <h3 className="text-xl md:text-2xl mb-2 font-serif" data-id="element-237">
                  Event Add-ons
                </h3>
                <p className="text-gray-500 text-sm mb-6" data-id="element-238">
                  Enhance your event with these optional extras.
                </p>

                <div className="space-y-3" data-id="element-239">
                  {availableAddons.map(addon => <div key={addon.id} className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 p-4 border border-gray-200 rounded-sm" data-id="element-240">
                      <span className="font-medium text-gray-700 text-sm sm:text-base" data-id="element-241">
                        {addon.name}
                      </span>
                      <div className="flex items-center justify-between sm:justify-start gap-3 bg-gray-50 rounded-sm p-1 border border-gray-200 w-full sm:w-auto" data-id="element-242">
                        <button onClick={() => updateQty('addon', addon.id, -1)} className="p-2 hover:text-brand-gold transition-colors" data-id="element-243">
                          <Minus size={16} data-id="element-244" />
                        </button>
                        <span className="w-8 text-center font-bold" data-id="element-245">
                          {addonQty[addon.id] || 0}
                        </span>
                        <button onClick={() => updateQty('addon', addon.id, 1)} className="p-2 hover:text-brand-gold transition-colors" data-id="element-246">
                          <Plus size={16} data-id="element-247" />
                        </button>
                      </div>
                    </div>)}
                </div>
              </motion.div>}

            {step === 4 && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} data-id="element-248">
                <h3 className="text-xl md:text-2xl mb-6 font-serif" data-id="element-249">
                  Review & Checkout
                </h3>

                <div className="bg-gray-50 p-6 rounded-sm border border-gray-200 mb-8" data-id="element-250">
                  <h4 className="font-bold uppercase tracking-widest text-xs text-gray-500 mb-4 border-b border-gray-200 pb-2" data-id="element-251">
                    Order Summary
                  </h4>

                  <div className="mb-4" data-id="element-252">
                    <p className="text-sm" data-id="element-253">
                      <span className="font-bold" data-id="element-254">Event:</span>{' '}
                      {formData.eventType} ({formData.attendees} Guests)
                    </p>
                    <p className="text-sm" data-id="element-255">
                      <span className="font-bold" data-id="element-256">Date:</span>{' '}
                      {formData.date || 'TBD'} |{' '}
                      <span className="font-bold" data-id="element-257">Location:</span>{' '}
                      {formData.location || 'TBD'}
                    </p>
                  </div>

                  <div className="space-y-2 mb-4" data-id="element-258">
                    <p className="font-bold text-sm" data-id="element-259">Packages:</p>
                    {cateringPackages.filter(p => packageQty[p.id]).map(p => <div key={p.id} className="flex justify-between text-sm text-gray-600" data-id="element-260">
                          <span data-id="element-261">
                            {packageQty[p.id]}x {p.name}
                          </span>
                          <span data-id="element-262">({packageQty[p.id] * p.serves} Servings)</span>
                        </div>)}
                    {Object.values(packageQty).every(v => v === 0) && <p className="text-sm text-red-500 italic" data-id="element-263">
                        No packages selected.
                      </p>}
                  </div>

                  {Object.values(addonQty).some(v => v > 0) && <div className="space-y-2 border-t border-gray-200 pt-4" data-id="element-264">
                      <p className="font-bold text-sm" data-id="element-265">Add-ons:</p>
                      {availableAddons.filter(a => addonQty[a.id]).map(a => <div key={a.id} className="flex justify-between text-sm text-gray-600" data-id="element-266">
                            <span data-id="element-267">
                              {addonQty[a.id]}x {a.name}
                            </span>
                          </div>)}
                    </div>}
                </div>

                <div className="space-y-4" data-id="element-268">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-269">
                    <input type="text" placeholder="Full Name" className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:border-brand-gold outline-none" onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} data-id="element-270" />
                    <input type="email" placeholder="Email Address" className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:border-brand-gold outline-none" onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} data-id="element-271" />
                  </div>
                  <textarea placeholder="Any special requests, dietary restrictions, or notes?" className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:border-brand-gold outline-none h-24 resize-none" onChange={e => setFormData({
                ...formData,
                notes: e.target.value
              })} data-id="element-272" />
                </div>
              </motion.div>}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-100 flex justify-between bg-gray-50 shrink-0" data-id="element-273">
            {step > 1 ? <button onClick={prevStep} className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-gray-500 hover:text-brand-black transition-colors" data-id="element-274">
                <ChevronLeft size={18} data-id="element-275" /> Back
              </button> : <div data-id="element-276" />}

            {step < 4 ? <button onClick={nextStep} disabled={step === 1 && (!formData.eventType || !formData.attendees) || step === 2 && Object.values(packageQty).every(v => v === 0)} className="bg-brand-black text-white px-8 py-3 rounded-sm text-xs font-bold uppercase tracking-widest hover:bg-brand-gold transition-all disabled:opacity-50 flex items-center gap-2" data-id="element-277">
                Next Step <ChevronRight size={18} data-id="element-278" />
              </button> : <button onClick={() => {
            alert('Order Request Submitted Successfully!');
            onClose();
          }} className="bg-brand-gold text-white px-10 py-3 rounded-sm text-xs font-bold uppercase tracking-widest hover:bg-brand-black transition-all flex items-center gap-2" data-id="element-279">
                <ShoppingBag size={18} data-id="element-280" /> Submit Order
              </button>}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}